import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { API_URL } from '../config';
import './AIMentor.css';

function AIMentor({ learningPath, userProgress }) {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [showSparkles, setShowSparkles] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // Sparkle animation on open
    useEffect(() => {
        if (isOpen) {
            setShowSparkles(true);
            const timer = setTimeout(() => setShowSparkles(false), 1000);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const quickQuestions = [
        { text: "What should I learn first?", icon: "🎯", category: "guidance" },
        { text: "Break this topic down for me", icon: "🧩", category: "explain" },
        { text: "Why is this important?", icon: "💡", category: "context" },
        { text: "Give me a study tip", icon: "✨", category: "tips" },
        { text: "I'm stuck, help me!", icon: "🆘", category: "help" },
        { text: "Quiz me on something", icon: "🧠", category: "quiz" }
    ];

    const getMentorPersonality = () => {
        const completedCount = userProgress?.completedModules?.length || 0;
        const totalModules = learningPath?.totalModules || 1;
        const progress = (completedCount / totalModules) * 100;

        if (progress === 0) return { mood: "excited", greeting: "Let's start this adventure!" };
        if (progress < 25) return { mood: "encouraging", greeting: "Great start! Keep going!" };
        if (progress < 50) return { mood: "proud", greeting: "You're making amazing progress!" };
        if (progress < 75) return { mood: "impressed", greeting: "Wow, you're crushing it!" };
        return { mood: "celebrating", greeting: "Almost there, champion!" };
    };

    const handleSendMessage = async (messageText = null) => {
        const question = messageText || input.trim();
        if (!question || isLoading) return;

        const userMessage = { role: 'user', content: question };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            // Build rich context about the learning path
            const context = {
                topic: learningPath?.topic,
                totalLevels: learningPath?.totalLevels,
                totalModules: learningPath?.totalModules,
                levels: learningPath?.levels?.map(level => ({
                    levelNumber: level.levelNumber,
                    title: level.title,
                    difficulty: level.difficulty,
                    description: level.description,
                    modules: level.modules?.map(m => ({
                        id: m.id,
                        title: m.title,
                        concepts: m.concepts,
                        completed: userProgress?.completedModules?.includes(m.id)
                    }))
                })),
                userProgress: {
                    completedModules: userProgress?.completedModules || [],
                    totalXP: userProgress?.totalXP || 0,
                    progressPercent: Math.round(((userProgress?.completedModules?.length || 0) / (learningPath?.totalModules || 1)) * 100)
                }
            };

            const response = await fetch(`${API_URL}/api/learning-mentor`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    question,
                    learningContext: context,
                    conversationHistory: messages.slice(-6) // Last 6 messages for context
                })
            });

            const data = await response.json();

            if (data.success) {
                const aiMessage = {
                    role: 'assistant',
                    content: data.answer,
                    suggestion: data.suggestion // Optional suggested action
                };
                setMessages(prev => [...prev, aiMessage]);
            } else {
                throw new Error(data.error || 'Failed to get response');
            }
        } catch (error) {
            console.error('Error:', error);
            const errorMessage = {
                role: 'assistant',
                content: "Oops! I had a little hiccup. Let me try again - ask me anything! 🤖"
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickQuestion = (question) => {
        handleSendMessage(question);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const personality = getMentorPersonality();

    const formatMessage = (content) => {
        // Simple markdown-like formatting
        const formatted = content
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/`(.*?)`/g, '<code>$1</code>')
            .replace(/\n/g, '<br/>');
        // Sanitize to prevent XSS attacks
        return DOMPurify.sanitize(formatted, {
            ALLOWED_TAGS: ['strong', 'em', 'code', 'br'],
            ALLOWED_ATTR: []
        });
    };

    return (
        <div className={`ai-mentor ${isOpen ? 'open' : ''} ${isMinimized ? 'minimized' : ''}`}>
            {/* Floating Button with pulse animation */}
            <button
                className={`ai-mentor-toggle ${isOpen ? 'active' : ''}`}
                onClick={() => {
                    if (isMinimized) {
                        setIsMinimized(false);
                    } else {
                        setIsOpen(!isOpen);
                    }
                }}
                title="AI Learning Mentor"
            >
                <span className="mentor-icon">
                    {isOpen ? '✕' : '🧙‍♂️'}
                </span>
                {!isOpen && (
                    <span className="mentor-pulse"></span>
                )}
                {!isOpen && messages.length === 0 && (
                    <span className="mentor-hint">Need help?</span>
                )}
            </button>

            {/* Chat Panel */}
            {isOpen && !isMinimized && (
                <div className="ai-mentor-panel">
                    {/* Sparkle effect */}
                    {showSparkles && (
                        <div className="sparkles">
                            <span className="sparkle">✨</span>
                            <span className="sparkle">⭐</span>
                            <span className="sparkle">💫</span>
                            <span className="sparkle">✨</span>
                        </div>
                    )}

                    {/* Header */}
                    <div className="ai-mentor-header">
                        <div className="mentor-avatar-container">
                            <div className="mentor-avatar">🧙‍♂️</div>
                            <div className={`mentor-status ${personality.mood}`}></div>
                        </div>
                        <div className="mentor-info">
                            <h3>AI Learning Mentor</h3>
                            <p className="mentor-status-text">{personality.greeting}</p>
                        </div>
                        <div className="mentor-actions">
                            <button
                                className="minimize-btn"
                                onClick={() => setIsMinimized(true)}
                                title="Minimize"
                            >
                                ─
                            </button>
                            <button
                                className="close-btn"
                                onClick={() => setIsOpen(false)}
                                title="Close"
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    {/* Topic Badge */}
                    {learningPath?.topic && (
                        <div className="topic-badge">
                            <span className="topic-icon">📚</span>
                            <span className="topic-text">{learningPath.topic}</span>
                            <span className="progress-chip">
                                {userProgress?.completedModules?.length || 0}/{learningPath?.totalModules || 0}
                            </span>
                        </div>
                    )}

                    {/* Messages */}
                    <div className="ai-mentor-messages">
                        {messages.length === 0 && (
                            <div className="welcome-section">
                                <div className="welcome-avatar">🧙‍♂️</div>
                                <h4>Hey there, learner! 👋</h4>
                                <p>
                                    I'm your AI learning mentor. I know all about
                                    <strong> {learningPath?.topic || 'your learning path'}</strong> and I'm here to help you master it!
                                </p>
                                <div className="welcome-features">
                                    <div className="feature">
                                        <span>🎯</span>
                                        <span>Get personalized guidance</span>
                                    </div>
                                    <div className="feature">
                                        <span>🧩</span>
                                        <span>Break down complex topics</span>
                                    </div>
                                    <div className="feature">
                                        <span>💡</span>
                                        <span>Understand the "why"</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                className={`message ${msg.role === 'user' ? 'user-message' : 'mentor-message'}`}
                            >
                                {msg.role === 'assistant' && (
                                    <div className="message-avatar mentor">🧙‍♂️</div>
                                )}
                                <div className="message-bubble">
                                    <div
                                        className="message-content"
                                        dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                                    />
                                    {msg.suggestion && (
                                        <button
                                            className="suggestion-btn"
                                            onClick={() => handleSendMessage(msg.suggestion)}
                                        >
                                            {msg.suggestion} →
                                        </button>
                                    )}
                                </div>
                                {msg.role === 'user' && (
                                    <div className="message-avatar user">👤</div>
                                )}
                            </div>
                        ))}

                        {isLoading && (
                            <div className="message mentor-message">
                                <div className="message-avatar mentor">🧙‍♂️</div>
                                <div className="message-bubble">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Quick Questions */}
                    {messages.length === 0 && (
                        <div className="quick-questions-section">
                            <p className="quick-label">Try asking:</p>
                            <div className="quick-questions-grid">
                                {quickQuestions.map((q, index) => (
                                    <button
                                        key={index}
                                        className={`quick-btn ${q.category}`}
                                        onClick={() => handleQuickQuestion(q.text)}
                                        disabled={isLoading}
                                    >
                                        <span className="quick-icon">{q.icon}</span>
                                        <span className="quick-text">{q.text}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Input Area */}
                    <div className="ai-mentor-input">
                        <div className="input-container">
                            <textarea
                                ref={inputRef}
                                placeholder="Ask me anything about your learning..."
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={isLoading}
                                rows="1"
                            />
                            <button
                                onClick={() => handleSendMessage()}
                                disabled={!input.trim() || isLoading}
                                className="send-btn"
                            >
                                <span className="send-icon">✨</span>
                            </button>
                        </div>
                        <p className="input-hint">Press Enter to send • Shift+Enter for new line</p>
                    </div>
                </div>
            )}

            {/* Minimized state */}
            {isOpen && isMinimized && (
                <div className="ai-mentor-minimized" onClick={() => setIsMinimized(false)}>
                    <span className="minimized-avatar">🧙‍♂️</span>
                    <span className="minimized-text">Click to expand</span>
                    {messages.length > 0 && (
                        <span className="message-count">{messages.length}</span>
                    )}
                </div>
            )}
        </div>
    );
}

export default AIMentor;
